import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { AxiosHeaders } from 'axios';
import { useTranslation } from 'react-i18next';
import Typography from "@mui/material/Typography";
import { Box, Button, IconButton, Stack, TextField } from '@mui/material';
import CardItem from '../../models/Carditem';
import OrderDto from '../../models/OrderDto';
import OrderItemDto from '../../models/order/OrderItemDto';
import { useCardContent } from '../../provider/CardContentContext';
import { useRestaurantData } from '../../provider/RestaurantDataContext';
import { useSnackbarProps } from '../../provider/SnackbarContext';
import CustomTitleBox from '../centralComponents/CustomTitleBox';
import OrderItemExtra from '../../models/order/OrderItemExtra';
import OrderItemOptionGroup from '../../models/order/OrderItemOptionGroup';
import CartRow from './CartRow';
import { currencyFormat } from '../../utils/Utils';

function total(items: readonly CardItem[]): number {
    return items.map((item) => calcPrice(item)).reduce((sum, i) => sum + i, 0);
}

export function calcPrice(item: CardItem): number {
    let price = 0;
    item.selectedOptions.itemExtras.forEach((itemExtra) => price += itemExtra.price * itemExtra.amount);
    // there always has to exist one itemOption in 'itemOptions'
    item.selectedOptions.itemOptionGroups.forEach((itemOptionGroup) => price += itemOptionGroup.itemOptions[0].price);
    return price * item.quantity + item.price * item.quantity;
};

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

export default function CartPage() {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [message, setMessage] = useState('');
    const [blockOrder, setBlockOrder] = useState(false);
    const { showSnackbar } = useSnackbarProps();
    const { restaurantData, table, token, restaurantData: { qrMenuProperties, tables }, sessionId, expired, live } = useRestaurantData();
    const params = useParams();
    const navigate = useNavigate();
    const { cardItems, clearCart } = useCardContent();

    const totalPrice = useCallback(
        () => {
            return total(cardItems);
        },
        [cardItems],
    );

    const goBack = () => {
        navigate(-1);
    }

    const mapCartItemExtras = useCallback((item: CardItem): OrderItemExtra[] => {
        return item.selectedOptions.itemExtras.map(extra => new OrderItemExtra(extra.id, extra.amount))
    }, []);

    const mapCartItemOptions = useCallback((item: CardItem): OrderItemOptionGroup[] => {
        // there always has to exist one itemOption in 'itemOptions'
        return item.selectedOptions.itemOptionGroups.map(optionGroup => new OrderItemOptionGroup(optionGroup.id, optionGroup.itemOptions[0].id));
    }, []);

    const order = () => {
        if (blockOrder || !qrMenuProperties?.dofEnabled || (table <= 0 && token.length === 0)) return;
        setBlockOrder(true);
        const orderItems = cardItems.map((item) => new OrderItemDto(item.id, item.quantity, mapCartItemExtras(item), mapCartItemOptions(item)));
        //TODO tableId richtig setzen
        let dataToSend: OrderDto = new OrderDto(Number.parseInt(params.restaurantId || '0'), table, tables.filter(tableCurrent => tableCurrent.id === table)[0]?.tableNumber ?? -1, orderItems, name, address, message);
        //orderItems.forEach((item) => dataToSend.items.push(item));
        const headers = new AxiosHeaders();
        headers.setAuthorization('Bearer ' + token);
        axios.post(backendUrl + '/restaurants/' + params.restaurantId + `/orders?${sessionId && sessionId.length > 0 ? `sessionId=${sessionId}` : ''}`, dataToSend, {
            headers
        }).then(
            (_response) => {
                //console.log('ordered', _response.status);
                if (_response.status - 200 >= 0 && _response.status - 200 < 100) {
                    showSnackbar({ snackbarText: t('snackbarOrderSent') });
                    clearCart();
                    setName('');
                    setMessage('');
                    setAddress('');
                } else {
                    showSnackbar({ snackbarText: t('snackbarFailedToSendOrder'), severity: 'error' });
                }
                setBlockOrder(false);
            },
            (error) => {
                const errorMessage = error.response.data.errorMessage;
                showSnackbar({ snackbarText: errorMessage, severity: 'error' });
                setBlockOrder(false);
            }
        );
    }

    return (
        <CustomTitleBox title={t('myOrder', { tableNumber: restaurantData.tables.find(t => t.id === table)?.tableNumber })}>
            {!qrMenuProperties?.dofEnabled && <Typography textAlign={'center'} variant="h4">{t('orderingDisabled')}</Typography>}
            {qrMenuProperties?.dofEnabled && !live && (table > 0 || token.length > 0) &&
                <Box>
                    <Stack direction='row' justifyContent='space-between'>
                        <IconButton sx={{ color: 'white', mt: '0rem', mb: '0.8rem' }} onClick={() => goBack()}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Button variant="outlined" disabled={cardItems.length === 0} onClick={() => clearCart()}>
                            {t('clearCartBtn')}
                        </Button>
                    </Stack>
                    <Box sx={{ mt: '1rem' }}>
                        <TableContainer component={Paper}>
                            <Table sx={{ maxWidth: '100vw' }} aria-label="spanning table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('orderNumber')}</TableCell>
                                        <TableCell>{t('orderDescription')}</TableCell>
                                        <TableCell align="center">{t('orderQuantity')}</TableCell>
                                        <TableCell align="right">{`${t('orderSum')} ${restaurantData.currency.detail}`}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cardItems.map((item, index) => (
                                        <CartRow key={item.id} index={index} item={item} />
                                    ))}
                                    <TableRow >
                                        <TableCell colSpan={1} />
                                        <TableCell style={{ paddingTop: '2rem' }} colSpan={2} align='center'>
                                            {t('orderTotalSum')}
                                        </TableCell>
                                        <TableCell style={{ paddingTop: '2rem', fontWeight: 'bold' }} align="right">
                                            {`${currencyFormat(totalPrice())}${restaurantData.currency.detail}`}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    <Stack direction="column" justifyContent="center" alignItems="center" spacing={1}>
                        {token.length > 0 &&
                            <>
                                <TextField
                                    error={name.length <= 0}
                                    id="order-name"
                                    label={t('qrrDofName')}
                                    fullWidth
                                    sx={{ backgroundColor: 'white', mt: '2rem' }}
                                    value={name}
                                    required
                                    helperText={name.length <= 0 ? 'Required' : ''}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setName(event.target.value);
                                    }}
                                />
                                <TextField
                                    id="order-address"
                                    label={t('qrrDofAddress')}
                                    fullWidth
                                    sx={{ backgroundColor: 'white', mt: '2rem' }}
                                    value={address}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setAddress(event.target.value);
                                    }}
                                />
                                <TextField
                                    id="order-name"
                                    label={t('qrrDofMessage')}
                                    fullWidth
                                    sx={{ backgroundColor: 'white' }}
                                    value={message}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setMessage(event.target.value);
                                    }}
                                />
                            </>
                        }
                        <Button variant="contained" sx={{ mt: '1rem' }} disabled={cardItems.length === 0 || (sessionId.length > 0 && expired.getTime() < Date.now())} onClick={() => order()}>
                            {t('orderBtn')}
                        </Button>
                        {sessionId.length > 0 && expired.getTime() < Date.now() && <Typography fontSize='0.8rem'>
                            {t("sessionExpiredInfo")}
                        </Typography>}
                        <Typography fontSize='0.8rem'>
                            {t('orderInformation')}
                        </Typography>
                    </Stack>
                </Box >}
        </CustomTitleBox >
    );
}